<template>
  <div class="grid-x flex-grow">
    <div class="cell auto cell-block-y flex flex-col flex-grow">
      <Loading :loading="waitPending">
        <div class="flex flex-col flex-grow">
          <div class="flex-grow">
            <CategoryCard
              v-for="category in categories" :key="category.id"
              :isEditLayout="false"
              @add-service="onAddServiceClick"
              @activate="onCategoryActivate"
              @deactivate="onCategoryDeactivate"
              @drag-start="onCategoryDragStart"
              @drag-end="onCategoryDragEnd"
              @drop-sort="onCategoryDrop"
              :dragging="+draggedCategoryId === +category.id"
              :category="category" />
          </div>
          <div class="flex flex-row items-end justify-center p-6">
            <ButtonDashed>
              <router-link class="flex items-center justify-center w-full h-full min-h-70px"
                           :to="{ name: 'services.categories.category.new' }">
                Добавить новую категорию услуг
              </router-link>
            </ButtonDashed>
          </div>
        </div>
      </Loading>
      <Modal
        id="add"
        :fullScreen="true"
        :hide-footer="true"
        :hideHeaderClose="false"
        >
        <template v-slot:modal-header-close>
          <img src="@/assets/icons/modal-header-close.svg" alt="modal-header-close" />
        </template>
        <template v-slot:modal-title>
          <div class="flex items-center w-full justify-between">
            <Input
              label="Добавьте название услуги:"
              placeholder="Введите название"
              classes="bg-hint w-110"
              :invalid="$v.service.name.$error"
              v-model="service.name"/>
            <button class="btn btn-sm btn-primary font-normal"
            @click="onServiceCreateClick(category.id)">Опубликовать услугу</button>
          </div>
        </template>
        <div slot class="flex flex-col pt-8">
          <div class="flex flex-col space-y-8 pl-48">
            <Input
              label="Категория услуг:"
              placeholder=""
              classes="bg-hint border-muted-light w-150"
              :disabled="true"
              :icon="category.iconAttach"
              v-model="category.name"/>
            <Input
              label="Описание услуги:"
              :textarea="true"
              placeholder="Введите описание услуги"
              classes="w-150"
              :invalid="$v.service.description.$error"
              v-model="service.description"/>
            <div class="flex flex-col w-96">
              <span class="flex text-sm text-muted mb-2">Визуал новой формы:</span>
              <Select
                :options="visualPresets"
                :label="'name'"
                :value="visualPresetByKey(service.visualKey)"
                :clearable="true"
                :searchable="true"
                @input="($event) => service.visualKey = $event ? $event.key : null"
                placeholder="Использовать визуал категории"/>
              <span class="text-12px text-gray-600 mt-2">
                Необязательно. Если не выбран, новая форма использует визуал категории.
              </span>
            </div>
            <Input
              label="Стоимость услуги:"
              placeholder="Введите стоимость"
              classes="bg-hint w-110"
              :inputmask="{
                alias: 'decimal',
                rightAlign: false,
                allowMinus: false,
                placeholder: '',
              }"
              :invalid="$v.service.price.$error"
              v-model="service.price"/>
            <div class="flex flex-col">
              <span class="flex text-sm text-muted mb-4">Тип товара:</span>
              <RadioButtonSet :options="countableOptions" v-model="service.countable"
              :invalid="$v.service.countable.$error" size="big" />
            </div>
            <div class="flex flex-col">
              <span class="flex text-sm text-muted mb-4">Обязательное поле для заказа:</span>
              <RadioButtonSet :options="requiredOptions" v-model="service.required"
              :invalid="$v.service.required.$error" size="big" />
            </div>
          </div>
          <div class="flex flex-col py-8 border-t border-solid border-light pl-48 mt-8">
            <Input
              label="Дополнительные опции:"
              placeholder="Укажите дополнительные опции"
              classes="bg-hint border-none w-110"
              v-model="optionName"
              @enter="onEnter"
              :options="additionalOptions"
              @option-delete="onOptionDelete"
              />
          </div>
          <div class="flex flex-col py-8 border-t border-solid border-light pl-48">
            <span class="flex text-sm text-muted mb-4">Показывать на экране Поиска:</span>
            <RadioButtonSet :options="showAtMobileSearchScreenOptions" size="big"
            :invalid="$v.service.showAtMobileSearchScreen.$error"
            v-model="service.showAtMobileSearchScreen" />
          </div>
          <div class="flex flex-col py-8 border-t border-solid border-light pl-48">
            <div class="max-w-300px pb-16">
              <span class="flex text-sm text-muted mb-4">Для каких объектов доступна услуга:</span>
              <Select
              :class="['bg-hint', {
                'border border-solid border-hint':
                  !$v.service.applicableTo.$dirty || !$v.service.applicableTo.$invalid,
                'border border-solid border-danger':
                  $v.service.applicableTo.$dirty && $v.service.applicableTo.$invalid,
              }]"
              :options="applicableToOptions"
              :label="'name'"
              :value="service.applicableTo"
              @input="($event) => service.applicableTo = $event"
              placeholder="Выберите доступность услуги"/>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import CategoryCard from '@/components/Services/CategoryCard.vue';
import ButtonDashed from '@/components/ButtonDashed.vue';
import Modal from '@/components/Modal.vue';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import RadioButtonSet from '@/components/RadioButtonSet.vue';
import Loading from '@/components/Loading.vue';
import { serviceVisualPresets, getServiceVisualPreset } from '@/constants/serviceVisualPresets';

export default {
  components: {
    CategoryCard,
    ButtonDashed,
    Modal,
    Input,
    Select,
    RadioButtonSet,
    Loading,
  },
  data() {
    return {
      category: {},
      draggedCategoryId: null,
      categoryOrderChanged: false,
      visualPresets: serviceVisualPresets,
      service: {
        name: null,
        description: null,
        price: null,
        visualKey: null,
        countable: null,
        required: null,
        showAtMobileSearchScreen: null,
        applicableTo: null,
        archive: false,
      },
      optionName: '',
      additionalOptions: [],
      countableOptions: [
        { name: 'Количественный товар', value: true },
        { name: 'Не количественный', value: false },
      ],
      requiredOptions: [
        { name: 'Обязательное', value: true },
        { name: 'Не обязательное', value: false },
      ],
      showAtMobileSearchScreenOptions: [
        { name: 'Показывать', value: true },
        { name: 'Не показывать', value: false },
      ],
      applicableToOptions: [
        {
          id: 1,
          name: 'Для всех Объектов',
          value: 'public',
        },
        {
          id: 2,
          name: 'Избранное',
          value: 'not_owned',
        },
        {
          id: 3,
          name: 'Только Владелец Объекта',
          value: 'owned',
        },
      ],
    };
  },
  validations: {
    service: {
      name: {
        required,
      },
      description: {
        required,
      },
      price: {
        required,
      },
      countable: {
        required,
      },
      required: {
        required,
      },
      showAtMobileSearchScreen: {
        required,
      },
      applicableTo: {
        required,
      },
    },
  },
  watch: {
    'service.applicableTo': {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      categories: (state) => state.services.categories,
      attach: (state) => state.service.attach,
    }),
    waitPending() {
      return this.$wait.is('fetch.service.categories')
        || this.$wait.is('fetch.service.categories.attach');
    },
  },
  methods: {
    ...mapActions(
      'services',
      [
        'ACT_FETCH_SERVICE_CATEGORIES_ATTACH',
        'ACT_FETCH_SERVICE_CATEGORIES',
        'ACT_CREATE_SERVICE',
        'ACT_CREATE_SERVICE_OPTION',
        'ACT_ACTIVATE_CATEGORY',
        'ACT_DEACTIVATE_CATEGORY',
        'ACT_SAVE_SERVICE_CATEGORIES_ORDER',
      ],
    ),
    ...mapMutations(
      'services',
      [
        'REORDER_SERVICE_CATEGORIES',
      ],
    ),
    moveId(ids, draggedId, targetId, after) {
      const result = ids.filter((id) => +id !== +draggedId);
      const targetIndex = result.findIndex((id) => +id === +targetId);

      if (targetIndex === -1) return ids;

      result.splice(targetIndex + (after ? 1 : 0), 0, draggedId);
      return result;
    },
    onCategoryDragStart(categoryId) {
      this.draggedCategoryId = categoryId;
      this.categoryOrderChanged = false;
    },
    onCategoryDrop({ id: targetId, after }) {
      if (!this.draggedCategoryId || +this.draggedCategoryId === +targetId) return;

      const currentIds = this.categories.map((category) => category.id);
      const nextIds = this.moveId(currentIds, this.draggedCategoryId, targetId, after);

      if (nextIds.join(',') !== currentIds.join(',')) {
        this.REORDER_SERVICE_CATEGORIES(nextIds);
        this.categoryOrderChanged = true;
      }
    },
    async onCategoryDragEnd() {
      const shouldSave = this.categoryOrderChanged;
      this.draggedCategoryId = null;
      this.categoryOrderChanged = false;

      if (shouldSave) {
        await this.ACT_SAVE_SERVICE_CATEGORIES_ORDER(this.categories.map((category) => category.id))
          .catch(() => {});
      }
    },
    visualPresetByKey(key) {
      return getServiceVisualPreset(key);
    },
    async getServiceCategories() {
      await this.ACT_FETCH_SERVICE_CATEGORIES();
    },
    async getServiceCategoriesAttach() {
      await this.ACT_FETCH_SERVICE_CATEGORIES_ATTACH();
    },
    onAddServiceClick(e, id) {
      this.resetValidation();
      this.clearServiceFields();
      this.category = this.categories.find((el) => el.id === id);
    },
    resetValidation() {
      this.$v.$reset();
    },
    clearServiceFields() {
      this.service = {
        name: null,
        description: null,
        price: null,
        visualKey: null,
        countable: null,
        required: null,
        showAtMobileSearchScreen: null,
        applicableTo: null,
        archive: false,
      };
    },
    async createService(id) {
      const {
        name,
        description,
        price,
        visualKey,
        countable,
        required: requiredObj,
        showAtMobileSearchScreen,
        archive,
        applicableTo,
      } = this.service;
      await this.ACT_CREATE_SERVICE({
        id,
        name,
        description,
        price,
        visualKey,
        countable: countable && countable.value,
        required: requiredObj && requiredObj.value,
        showAtMobileSearchScreen: showAtMobileSearchScreen && showAtMobileSearchScreen.value,
        archive,
        applicableTo: applicableTo && applicableTo.value,
      }).then((response) => {
        const { data: serviceId } = response;
        if (response && serviceId && this.additionalOptions && this.additionalOptions.length) {
          this.additionalOptions.forEach(({ name: optionName }) => {
            this.ACT_CREATE_SERVICE_OPTION({
              id,
              serviceId,
              name: optionName,
            });
          });
        }
      });
    },
    async onServiceCreateClick(id) {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        await this.createService(id).then(() => {
          this.getServiceCategories();
          this.$nextTick(() => {
            this.$bvModal.hide('add');
          });
        });
      }
    },
    onEnter(e) {
      if (this.optionName && this.optionName.length) {
        this.additionalOptions = [
          ...this.additionalOptions,
          { id: +new Date(), name: e },
        ];
      }
      this.optionName = '';
    },
    onOptionDelete(id) {
      this.additionalOptions = this.additionalOptions.filter((el) => el.id !== id);
    },
    onCategoryActivate(id) {
      this.ACT_ACTIVATE_CATEGORY(id);
    },
    onCategoryDeactivate(id) {
      this.ACT_DEACTIVATE_CATEGORY(id);
    },
  },
  mounted() {
    this.getServiceCategories();
    this.getServiceCategoriesAttach();
  },
};
</script>
